/**
 * SEO_BRIEF_12 section 4: the decisions behind /mp3-to-video, kept apart from the encoding so Node
 * can test them. An audio file and one still picture become an MP4: the picture as H.264 at a low
 * frame rate, the sound beside it. The worker (web/static/burn/src/still-video-worker.ts) asks the
 * browser what it can do and src/media/still-video.ts runs what this file decides.
 *
 * THE PICTURE does not move, so it is encoded at 2 frames a second with a key frame every 10
 * seconds: a phone makes an hour of it quickly and the file stays small. Two shapes, wide 16:9 at
 * 1280 × 720 for YouTube and square 1:1 at 1080 × 1080; the bitrate is a ceiling the encoder
 * rarely reaches on a picture that never changes.
 *
 * THE SOUND takes one of five routes, decided when the file is opened (`soundRoute`):
 *   copy    AAC already (an M4A, an AAC file): its packets go into the MP4 as they are.
 *   aac     anything this browser can play, re-encoded as AAC where the browser has an AAC
 *           encoder: 192 kbit/s stereo or 128 kbit/s mono, at 44.1 or 48 kHz (`aacSettings`).
 *   mp3     an MP3 where AAC can't be made: the MP3 itself goes into the MP4, which carries it.
 *   lame    anything else this browser can play where AAC can't be made (a WAV on an older iPhone,
 *           on Linux, on an Android phone without the encoder): the site's MP3 encoder (LAME, as
 *           /audio-converter uses it) at 192 kbit/s, and that MP3 into the MP4.
 *   refuse  a sound this browser can't play and the MP4 can't carry as it is.
 * An AAC encode that fails part way is run once more as `mp3` or `lame` (`fallbackRoute`). The
 * brief asks for AAC; the two MP3 routes are the closest true thing where a browser can't make it,
 * so a WAV is never turned away for want of an encoder.
 *
 * THE FRAMES are one picture repeated: `frameTime` says when each starts and how long it lasts,
 * a whole half second each, so the picture covers the sound and ends less than half a second after.
 */

import { convertedName } from './video-plan.ts';

export type StillShape = 'wide' | 'square';

export const STILL_SHAPES: readonly StillShape[] = ['wide', 'square'];

export interface ShapeSpec {
  readonly width: number;
  readonly height: number;
  /** The video bitrate asked of the encoder, in bits per second: a ceiling for a still picture. */
  readonly videoBitrate: number;
}

/** 400 kbit/s for 0.92 megapixels and 500 for 1.17: the same ceiling per pixel, near enough. */
export const SHAPES: Readonly<Record<StillShape, ShapeSpec>> = {
  wide: { width: 1280, height: 720, videoBitrate: 400_000 },
  square: { width: 1080, height: 1080, videoBitrate: 500_000 },
};

export const DEFAULT_SHAPE: StillShape = 'wide';

/** Frames a second: the picture doesn't move. */
export const STILL_FPS = 2;

/** Seconds between key frames, so a player can seek in a long video. */
export const KEY_FRAME_INTERVAL_S = 10;

/**
 * Seconds of picture added before the sound is let catch up (`still-video.ts` runs the two in
 * step), so the MP4 is written in order without holding much of either.
 */
export const STEP_S = 10;

/** The bitrate of the site's MP3 (the `lame` route), as /mp4-to-mp3 makes it. */
export const LAME_KBPS = 192;

export function isShape(value: unknown): value is StillShape {
  return value === 'wide' || value === 'square';
}

/* ── the sound ────────────────────────────────────────────────────────── */

export type SoundRoute = 'copy' | 'aac' | 'mp3' | 'lame' | 'refuse';

export interface SoundFacts {
  /** The codec as mediabunny names it: `aac`, `mp3`, `pcm-s16`, `opus`…; null when unknown. */
  readonly codec: string | null;
  /** This browser can decode the sound (PCM always can: mediabunny decodes it itself). */
  readonly canDecode: boolean;
  /** This browser can encode AAC at `aacSettings` for this sound. */
  readonly aac: boolean;
}

/** How the sound gets into the MP4. */
export function soundRoute(facts: SoundFacts): SoundRoute {
  if (facts.codec === 'aac') return 'copy';
  if (facts.canDecode && facts.aac) return 'aac';
  if (facts.codec === 'mp3') return 'mp3';
  if (facts.canDecode) return 'lame';
  return 'refuse';
}

/** The route a failed AAC encode runs again on, or null when there is none. */
export function fallbackRoute(
  facts: Pick<SoundFacts, 'codec' | 'canDecode'>,
): 'mp3' | 'lame' | null {
  if (facts.codec === 'mp3') return 'mp3';
  if (facts.canDecode) return 'lame';
  return null;
}

export interface AacSettings {
  readonly numberOfChannels: 1 | 2;
  readonly sampleRate: 44_100 | 48_000;
  readonly bitrate: number;
}

/**
 * The AAC the `aac` route makes of a sound: at most two channels (a 5.1 film mix becomes stereo),
 * 44.1 kHz kept, every other rate made 48 kHz, which every AAC encoder takes.
 */
export function aacSettings(sound: { numberOfChannels: number; sampleRate: number }): AacSettings {
  const numberOfChannels = sound.numberOfChannels >= 2 ? 2 : 1;
  return {
    numberOfChannels,
    sampleRate: sound.sampleRate === 44_100 ? 44_100 : 48_000,
    bitrate: numberOfChannels === 1 ? 128_000 : 192_000,
  };
}

/* ── the frames ───────────────────────────────────────────────────────── */

/** How many frames cover `durationS` seconds of sound: the last one may be short. */
export function frameCount(durationS: number, fps: number = STILL_FPS): number {
  if (!(durationS > 0) || !Number.isFinite(durationS)) return 1;
  return Math.max(1, Math.ceil(durationS * fps - 1e-9));
}

export interface FrameTime {
  readonly timestamp: number;
  readonly duration: number;
}

/**
 * When frame `index` starts and how long it lasts: every frame a whole step. The track's times
 * are held to its frame rate (mediabunny snaps them), so a last frame cut short would be lost;
 * whole frames cover the sound and end less than one step after it.
 */
export function frameTime(index: number, fps: number = STILL_FPS): FrameTime {
  const step = 1 / fps;
  return { timestamp: index * step, duration: step };
}

/** The frames that start before `until` seconds, from frame `from` on: one step of the run. */
export function framesBefore(from: number, until: number, durationS: number): number {
  const count = frameCount(durationS);
  let end = from;
  while (end < count && end / STILL_FPS < until - 1e-9) end += 1;
  return end;
}

/* ── names ────────────────────────────────────────────────────────────── */

/** The longest title the field starts with: a file name past this is a path, not a title. */
export const TITLE_MAX = 120;

/**
 * The title a picture-less video starts with: the file's name without its type, underscores read
 * as spaces ("my_song_final.mp3" → "my song final"). The visitor edits it before making the video.
 */
export function defaultTitle(fileName: string): string {
  const base = fileName.replace(/\.[^./\\]+$/, '');
  const words = base.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
  return (words || base.trim()).slice(0, TITLE_MAX);
}

/** "song.mp3" → "song.mp4": the audio's name with the video's type, as /mp4-converter names it. */
export function stillName(fileName: string): string {
  return convertedName(fileName);
}
